import React, { useContext, useState } from "react";
import { useTheme, Grid, Typography, Button } from "@mui/material";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import FieldSection, { Field } from "../../components/general/FieldSection";
import useCompanies from "../../hooks/companies/useCompanies";
import { CreateCompanyDto } from "../../types/companyTypes";
import useIndustries from "../../hooks/companies/useIndustries";

/**
 * Page for creating a new company
 */
const CreateCompany: React.FunctionComponent = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  /**
   * Function which provides the styles of the CreateCompany
   */
  const styles = {
    button: {
      marginTop: "20px",
      marginBottom: "20px",
      border: "0",
      backgroundColor: theme.palette.primary.main,
      color: "white",
      "&:hover": {
        color: "black",
      },
    },
  };

  const { auth } = useContext(AuthContext);
  const hasCrmPermissions = doesPermissionsHaveSomeOf(auth.permissions, [13]);
  const { createCompany } = useCompanies();
  const { allIndustries } = useIndustries();

  const [newCompany, setNewCompany] = useState<CreateCompanyDto>({
    name: "",
    industry: { industryId: -1, description: "" },
    shortDescription: "",
    street: "",
    postalCode: "",
    city: "",
    addressAdditional: "",
    url: "",
    importantInformation: "",
    contactDesired: false,
    classified: false,
  });

  const [isNameFieldEmpty, setIsNameFieldEmpty] = useState<boolean>(false);
  const [isIndustryFieldEmpty, setIsIndustryFieldEmpty] = useState<boolean>(false);
  const [isPostalCodeFieldInvalid, setIsPostalCodeFieldInvalid] = useState<boolean>(false);

  /**
   * Handles the industry selection
   */
  const handleIndustryChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const industryId = parseInt(event.target.value);
    const selectedIndustry = allIndustries.find((industry) => industry.industryId === industryId);
    if (selectedIndustry !== undefined) {
      setNewCompany((prev) => ({ ...prev, industry: selectedIndustry }));
    }
  };

  /**
   * Submits the inputted company data if critical fields are not empty.
   */
  const handleSubmit = async () => {
    if (
      newCompany.name.trim() === "" ||
      newCompany.industry.industryId === -1 ||
      (newCompany.postalCode.trim() !== "" && newCompany.postalCode.length > 10)
    ) {
      setIsNameFieldEmpty(newCompany.name.trim() === "");
      setIsIndustryFieldEmpty(newCompany.industry.industryId === -1);
      setIsPostalCodeFieldInvalid(newCompany.postalCode.trim() !== "" && newCompany.postalCode.length > 10);
      return;
    }

    setIsNameFieldEmpty(false);
    setIsIndustryFieldEmpty(false);
    setIsPostalCodeFieldInvalid(false);

    const createdCompany = await createCompany(newCompany);

    if (createdCompany) {
      navigate(`/unternehmen/${createdCompany.companyId}`);
    }
  };

  const fields: Array<Field> = [
    {
      label: "Name",
      state: newCompany.name,
      width: "full",
      onChangeCallback: (e) => setNewCompany((prev) => ({ ...prev, name: e.target.value })),
      type: "Text",
      helperText: isNameFieldEmpty ? "Bitte gib einen Namen ein!" : undefined,
      error: isNameFieldEmpty,
    },
    {
      label: "Branche",
      state: newCompany.industry.industryId,
      width: "full",
      onChangeCallback: handleIndustryChange,
      type: "Dropdown",
      values: allIndustries.map((industry) => ({
        value: industry.industryId,
        label: industry.description,
      })),
      helperText: isIndustryFieldEmpty ? "Bitte gib eine Branche ein!" : undefined,
      error: isIndustryFieldEmpty,
    },
    {
      label: "Kurzbeschreibung",
      state: newCompany.shortDescription,
      width: "full",
      onChangeCallback: (e) => setNewCompany((prev) => ({ ...prev, shortDescription: e.target.value })),
      type: "TextBig",
      rows: 4,
    },
    {
      label: "Adresszusatz",
      state: newCompany.addressAdditional,
      width: "full",
      onChangeCallback: (e) => setNewCompany((prev) => ({ ...prev, addressAdditional: e.target.value })),
      type: "Text",
    },
    {
      label: "Straße/Nr",
      state: newCompany.street,
      width: "full",
      onChangeCallback: (e) => setNewCompany((prev) => ({ ...prev, street: e.target.value })),
      type: "Text",
    },
    {
      label: "PLZ",
      state: newCompany.postalCode,
      width: "full",
      onChangeCallback: (e) => setNewCompany((prev) => ({ ...prev, postalCode: e.target.value })),
      type: "Text",
      helperText: isPostalCodeFieldInvalid ? "Bitte gib eine gültige Postleitzahl ein!" : undefined,
      error: isPostalCodeFieldInvalid,
    },
    {
      label: "Ort",
      state: newCompany.city,
      width: "full",
      onChangeCallback: (e) => setNewCompany((prev) => ({ ...prev, city: e.target.value })),
      type: "Text",
    },
    {
      label: "Webseite",
      state: newCompany.url,
      width: "full",
      onChangeCallback: (e) => setNewCompany((prev) => ({ ...prev, url: e.target.value })),
      type: "Text",
    },
    {
      label: "Wichtige Informationen",
      state: newCompany.importantInformation,
      width: "full",
      onChangeCallback: (e) => setNewCompany((prev) => ({ ...prev, importantInformation: e.target.value })),
      type: "TextBig",
      rows: 4,
    },
    {
      label: "Kontakt erwünscht",
      state: newCompany.contactDesired,
      width: "full",
      onChangeCallback: (e) => setNewCompany((prev) => ({ ...prev, contactDesired: e.target.checked })),
      type: "Checkbox",
    },
    {
      label: "Geheim",
      state: newCompany.classified,
      width: "full",
      onChangeCallback: (e) => setNewCompany((prev) => ({ ...prev, classified: e.target.checked })),
      type: "Checkbox",
    },
  ];

  return (
    <div>
      {hasCrmPermissions && (
        <Grid container spacing={1} sx={{ paddingTop: "30px", paddingLeft: "10px" }}>
          <Grid item>
            <Button sx={styles.button} variant="contained" component={Link} to={`/unternehmen/`}>
              Zurück
            </Button>
          </Grid>
          <Grid item>
            <FieldSection title={"Neuer Kunde"} fields={fields}></FieldSection>
          </Grid>
          <Grid item xs={12} sx={{ paddingLeft: theme.spacing(2), paddingTop: theme.spacing(1) }}>
            <Typography variant="h5" sx={{ marginBottom: theme.spacing(1) }}>
              Ansprechpersonen
            </Typography>
            <Typography color="text.secondary">
              Ansprechpersonen können nach dem Erstellen des Unternehmens hinzugefügt werden.
            </Typography>
          </Grid>
          <Grid item>
            <Button sx={styles.button} variant="contained" onClick={handleSubmit}>
              Kunden erstellen
            </Button>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default CreateCompany;
